import { Icon } from "@bluframe/blublocks"
import useToggle from "@bluframe/grapple/useToggle"
import Typography from "components/Typography"
import type { ReactNode } from "react"

import type { ComponentProps } from "."
import {
  ActivityItem,
  ActivityList,
  ActivityToggleButton,
  Wrapper
} from "./styled"

const timestampFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  month: "short",
  timeZone: "UTC",
  year: "numeric"
})

const formatTimestamp = (iso: string) => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return timestampFormatter.format(date)
}

const getJobLabel = (job: ComponentProps["activity"][number]["job"]) =>
  job === "discoveryIngest" ? "Discovery ingest" : "Follower sync"

const getStatusLabel = (
  status: ComponentProps["activity"][number]["status"]
): string => {
  if (status === "queued") return "queued"
  if (status === "conflict") return "already running"
  return "failed"
}

const JobTriggerActivity = ({ activity }: ComponentProps) => {
  const [isExpanded, toggleIsExpanded] = useToggle(false)

  const activityLabel = activity.length
    ? `Activity log (${activity.length})`
    : "Activity log"

  const iconName = isExpanded ? "IoChevronUp" : "IoChevronDown"

  let content: ReactNode = null
  if (isExpanded) {
    if (activity.length) {
      content = (
        <ActivityList aria-label="Activity log list">
          {activity.map((entry) => (
            <ActivityItem key={entry.id}>
              <Typography.Paragraph>
                {formatTimestamp(entry.at)}

                {" · "}

                {entry.user}

                {" · "}

                {`${getJobLabel(entry.job)} ${getStatusLabel(entry.status)}`}

                {entry.message ? ` — ${entry.message}` : null}
              </Typography.Paragraph>
            </ActivityItem>
          ))}
        </ActivityList>
      )
    } else {
      content = (
        <Typography.Paragraph>
          {"No job triggers recorded yet."}
        </Typography.Paragraph>
      )
    }
  }

  return (
    <Wrapper aria-label="Job trigger activity" role="region">
      <ActivityToggleButton
        expanded
        iconEnd={<Icon name={iconName} />}
        label={activityLabel}
        onClick={toggleIsExpanded}
        transparent
      />

      {content}
    </Wrapper>
  )
}

export default JobTriggerActivity
